/**
 * Client mirror of netlify/functions/_lib/equipment-interest.ts.
 *
 * Same pairing convention this repo already uses for will-call (1.97) and
 * delivery schedules (1.101): the server file is the source of truth and
 * validates on submit; this one exists so the form can render the choices
 * without a round-trip. Keep the CATEGORY KEYS identical — they are what the
 * server matches on, and a key that only exists here is silently dropped.
 *
 * The categories are ERLS's own equipment categories, so the quote an operator
 * builds in the leasing app uses the same words the customer picked.
 */

export interface EquipmentChoice {
  category: string;
  label: string;
  hint: string;
}

export const EQUIPMENT_CHOICES: EquipmentChoice[] = [
  {
    category: 'fountain',
    label: 'Fountain dispenser',
    hint: 'Self-serve or crew-serve soda tower with an ice bin.',
  },
  {
    category: 'bargun',
    label: 'Bar gun',
    hint: 'Handheld gun behind the bar or drive-thru window.',
  },
  {
    category: 'ice_machine',
    label: 'Ice machine',
    hint: 'Makes the ice. A dispenser holds ice but does not make it.',
  },
  {
    category: 'refrigeration',
    label: 'Refrigeration',
    hint: 'Coolers, back-bar boxes, walk-in fans.',
  },
  {
    category: 'warehouse',
    label: 'Syrup rack / BIB storage',
    hint: 'Racking and pumps for bag-in-box syrup.',
  },
  {
    category: 'accessory',
    label: 'CO₂ setup',
    hint: 'Cylinders, regulators and lines for carbonation.',
  },
  {
    category: 'service_contract',
    label: 'Service plan',
    hint: 'Scheduled preventive maintenance and priority service.',
  },
];

export interface EquipmentSelection {
  category: string;
  qty: number;
}

/**
 * The ERLS equipment-lease application, prefilled with what the applicant just
 * typed.
 *
 * ⚠ NOT rendered on /apply/:token any more, deliberately — foodservice
 * equipment is requisitioned in the foodservice portal (melt-dashboard) and
 * ERLS owns only the lease; a store manager should not land in a credit
 * application from a signup form. Kept for the STAFF-triggered lease path.
 * See the server mirror and docs/HANDOFF-FOODSERVICE-ERLS-SEAM.md.
 *
 * `bn` / `cn` / `ce` is ERLS's whole prefill surface; there is no param for
 * the equipment selection, so that travels on the application record.
 */
export function erlsApplicationUrl(input: {
  businessName?: string;
  contactName?: string;
  contactEmail?: string;
}): string {
  const base = (
    (import.meta.env.VITE_ERLS_APPLICATION_URL as string | undefined) ??
    'https://alamedapointbg.com/equipment.app'
  ).replace(/\/$/, '');
  const q = new URLSearchParams();
  if (input.businessName?.trim()) q.set('bn', input.businessName.trim());
  if (input.contactName?.trim()) q.set('cn', input.contactName.trim());
  if (input.contactEmail?.trim()) q.set('ce', input.contactEmail.trim());
  const qs = q.toString();
  return qs ? `${base}?${qs}` : base;
}
